import React, { useState } from "react";
import { useCookies } from "react-cookie";
import DocumentMeta from "react-document-meta";
import { BiCog } from "react-icons/bi";
import { BsToggles } from "react-icons/bs";
import { CgNotes, CgSandClock } from "react-icons/cg";
import { RiDeleteBin6Line, RiDraftLine } from "react-icons/ri";
import { useJwt } from "react-jwt";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import PropTypes from "prop-types"; // Added import
import AuthorCardLarge from "../../../../components/elements/AuthorCardLarge";
import ProfileCard from "../../../../components/elements/ProfileCard";
import ProtectedRoute from "../../../../components/functions/ProtectedRoute";
import AuthorSubmitted from "../author/AuthorSubmitted";
import Drafts from "../author/Drafts";
import PostMenu from "../author/PostMenu";
import Posts from "../author/Posts";
import Withdrawn from "../author/Withdrawn";
import ProfileSettings from "./ProfileSettings";

const publicNavigation = [
  { name: "Settings", href: "/phis/user/settings", icon: <BiCog className="h-5 w-5" /> },
];

const privateNavigation = [
  { name: "My Posts", href: "/phis/user/posts", icon: <CgNotes className="h-5 w-5" /> },
  { name: "Drafts", href: "/phis/user/drafts", icon: <RiDraftLine className="h-5 w-5" /> },
//   { name: "Pending", href: "/phis/user/submitted", icon: <CgSandClock className="h-5 w-5" /> },
  { name: "Deleted", href: "/phis/user/deleted", icon: <RiDeleteBin6Line className="h-5 w-5" /> },
  { name: "Settings", href: "/phis/user/settings", icon: <BiCog className="h-5 w-5" /> },
];

const adminNavigation = [
  { name: "My Posts", href: "/phis/user/posts", icon: <CgNotes className="h-5 w-5" /> },
  { name: "Drafts", href: "/phis/user/drafts", icon: <RiDraftLine className="h-5 w-5" /> },
  // { name: "Pending", href: "/phis/user/submitted", icon: <CgSandClock className="h-5 w-5" /> },
  { name: "Deleted", href: "/phis/user/deleted", icon: <RiDeleteBin6Line className="h-5 w-5" /> },
  { name: "Settings", href: "/phis/user/settings", icon: <BiCog className="h-5 w-5" /> },
  { name: "Dashboard", href: "/dash", icon: <BsToggles className="h-5 w-5" /> },
];

const meta = {
  title: "Profile | PHIS",
  description: "User Profile",
  canonical: "https://fedgen.net/phis/user",
  meta: {
    charset: "utf-8",
    name: {
      keywords: "PHIS,public,health,capic-ace,Africa",
    },
  },
};

export default function Profile(props) {
  const [isActive, setActive] = useState(0);
  const [cookies] = useCookies([]);
  const { isExpired } = useJwt(cookies["plt"]);
  const location = useLocation();

  if (isExpired || !cookies["plt"]) {
    return <Navigate to="/auth/signin" state={{ from: location }} replace />;
  }

  const onClickNav = (idx) => {
    setActive(idx);
  };

  const getNavigation = () => {
    switch (props.user_role) {
      case "A":
      case "S":
      case "R":
        return adminNavigation;
      case "P":
        return publicNavigation;
      default:
        return publicNavigation;
    }
  };

  const navigation = getNavigation();

  return (
    <>
      <DocumentMeta {...meta} />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Profile Header */}
          <div className="mb-8">
            <ProfileCard id={props.data.unique_id} />
          </div>

          {/* Navigation Tabs */}
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <nav className="flex border-b border-gray-200">
              {navigation.map((nav, idx) => (
                <Link
                  key={nav.href}
                  to={nav.href}
                  onClick={() => onClickNav(idx)}
                  className={`flex items-center px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                    idx === isActive
                      ? "bg-blue-50 text-blue-900 border-b-2 border-blue-900"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                  aria-current={idx === isActive ? "page" : undefined}
                  aria-label={`Navigate to ${nav.name}`}
                >
                  {nav.icon}
                  <span className="ml-2">{nav.name}</span>
                </Link>
              ))}
            </nav>

            {/* Content Area */}
            <div className="p-6">
              <Routes>
                <Route
                  path="/posts/*"
                  element={
                    <ProtectedRoute
                      isAllowed={!isExpired && props.user_role !== "P"}
                    >
                      <PostMenu
                        user={props.data}
                        role={props.user_role}
                        user_id={props.user_id}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/drafts"
                  element={
                    <ProtectedRoute
                      isAllowed={!isExpired && props.user_role !== "P"}
                    >
                      <Drafts user={props.data} role={props.user_role} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/submitted"
                  element={
                    <ProtectedRoute
                      isAllowed={!isExpired && props.user_role !== "P"}
                    >
                      <AuthorSubmitted user={props.data} role={props.user_role} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/deleted"
                  element={
                    <ProtectedRoute
                      isAllowed={!isExpired && props.user_role !== "P"}
                    >
                      <Withdrawn user={props.data} role={props.user_role} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute isAllowed={!isExpired && props.user_role}>
                      <ProfileSettings data={props.data} role={props.user_role} />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

Profile.propTypes = {
  data: PropTypes.shape({
    unique_id: PropTypes.string.isRequired,
  }).isRequired,
  user_role: PropTypes.string,
  user_id: PropTypes.string,
};